import { useMemo } from "react";
import { HiOutlineChevronDown, HiOutlineLockClosed } from "react-icons/hi";
import { useSearchParams } from "react-router-dom";

import { Sidenav } from "../../components";
import { HubspotProvider } from "../../contexts";
import { useAuth, useFeatureFlag, FEATURE_FLAGS } from "../../hooks";
import { HubspotIntegration } from "../SettingsPage/IntegrationsTab/HubspotIntegration";
import { MixpanelIntegration } from "../SettingsPage/IntegrationsTab/MixpanelIntegration";
import { AmplitudeIntegration } from "../SettingsPage/IntegrationsTab/AmplitudeIntegration";
import { PostHogIntegration } from "../SettingsPage/IntegrationsTab/PostHogIntegration";
import { SlackIntegration } from "../SettingsPage/IntegrationsTab/SlackIntegration";
import { INTEGRATION_CATEGORIES, activeService } from "./catalog";

// Integrations settings — "Master-detail" layout. The left rail is an accordion:
// clicking a category expands its provider list; picking a provider renders the
// REAL integration component (HubSpot, Mixpanel, Amplitude, Slack) on the right.
//
// The selected provider lives in the URL (?integration=<id>) so the view is
// shareable/deep-linkable; the expanded category is derived from it.

const FLAG_GATED_CATEGORIES = {
  product_analytics: FEATURE_FLAGS.PRODUCT_USAGE_INTEGRATION,
};

const buildProviderCategory = (categories) =>
  Object.fromEntries(
    categories.flatMap((c) => c.services.map((s) => [s.id, c.key])),
  );

const findDefaultProvider = (categories) =>
  categories.flatMap((c) => c.services).find((s) => s.status === "connected")
    ?.id || null;

// Provider id -> real integration component. HubSpot needs its own provider
// (the others rely on the app-wide IntegrationsProvider from index.js).
const renderIntegration = (providerId) => {
  switch (providerId) {
    case "hubspot":
      return (
        <HubspotProvider>
          <HubspotIntegration />
        </HubspotProvider>
      );
    case "mixpanel":
      return <MixpanelIntegration />;
    case "amplitude":
      return <AmplitudeIntegration />;
    case "posthog":
      return <PostHogIntegration />;
    case "slack":
      return <SlackIntegration />;
    default:
      return null;
  }
};

const RailCategory = ({
  category,
  isExpanded,
  selectedProviderId,
  onToggle,
  onSelectProvider,
}) => (
  <div>
    <button
      type="button"
      onClick={() => onToggle(category)}
      className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-3 text-left transition-colors ${
        isExpanded ? "bg-gray-50" : "hover:bg-gray-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <category.icon className="h-5 w-5 shrink-0 text-gray-400" />
        <span className="font-medium text-gray-900">{category.label}</span>
      </div>
      <HiOutlineChevronDown
        className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
          isExpanded ? "" : "-rotate-90"
        }`}
      />
    </button>

    {isExpanded && (
      <div className="animation-slide-down-lil-bounce my-2 ml-4 space-y-1 border-l border-gray-200 pl-2">
        {category.services.map((service) => (
          <button
            key={service.id}
            type="button"
            onClick={() => onSelectProvider(service.id)}
            className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
              service.id === selectedProviderId
                ? "bg-blue-50 font-medium text-blue-600"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <service.logo className="h-4 w-4 shrink-0" />
            {service.name}
          </button>
        ))}
      </div>
    )}
  </div>
);

export const Integrations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { team } = useAuth();
  // Changing integrations is admin-only on the API (PAN-824), so a member sees each
  // integration's state with its controls disabled, instead of buttons that fail.
  const canEdit = team?.is_admin === true;
  const productUsageEnabled = useFeatureFlag(
    FEATURE_FLAGS.PRODUCT_USAGE_INTEGRATION,
  );

  const visibleCategories = useMemo(
    () =>
      INTEGRATION_CATEGORIES.filter((c) => {
        const flag = FLAG_GATED_CATEGORIES[c.key];
        return !flag || productUsageEnabled;
      }),
    [productUsageEnabled],
  );

  const providerCategory = useMemo(
    () => buildProviderCategory(visibleCategories),
    [visibleCategories],
  );
  const defaultProviderId = useMemo(
    () => findDefaultProvider(visibleCategories),
    [visibleCategories],
  );

  const param = searchParams.get("integration");
  const selectedProviderId = providerCategory[param]
    ? param
    : defaultProviderId;
  const expandedKey = selectedProviderId
    ? providerCategory[selectedProviderId]
    : null;

  const setProvider = (providerId) => {
    const next = new URLSearchParams(searchParams);
    if (providerId) {
      next.set("integration", providerId);
    } else {
      next.delete("integration");
    }
    setSearchParams(next, { replace: true });
  };

  const handleToggle = (category) => {
    if (expandedKey === category.key) {
      setProvider(null); // collapse + clear the URL
      return;
    }
    // Expand the category and bring its default provider into view.
    setProvider((activeService(category) || category.services[0]).id);
  };

  return (
    <div className="flex min-h-full flex-1 flex-row">
      <Sidenav />
      <div className="flex h-screen flex-1 flex-col overflow-y-scroll">
        <div className="animation-slide-up-lil-bounce m-5 flex flex-1 flex-col px-1">
          <div className="mb-1 text-4xl font-bold leading-10 text-black">
            Integrations
          </div>
          <p className="mb-6 mt-1 text-sm text-gray-500">
            Pick a category on the left to manage its source.
          </p>

          <div className="flex flex-col gap-6 md:flex-row">
            {/* Rail (accordion) — self-start so it keeps its content height
                instead of stretching to match a tall detail pane */}
            <div className="sticky top-5 w-full shrink-0 space-y-1 self-start rounded-lg border border-gray-200 bg-white p-2 shadow-sm md:w-72">
              {visibleCategories.map((category) => (
                <RailCategory
                  key={category.key}
                  category={category}
                  isExpanded={expandedKey === category.key}
                  selectedProviderId={selectedProviderId}
                  onToggle={handleToggle}
                  onSelectProvider={setProvider}
                />
              ))}
            </div>

            {/* Detail — keyed so the component animates in on every switch */}
            <div className="flex-1">
              {selectedProviderId ? (
                <div
                  key={selectedProviderId}
                  className="animation-slide-up-lil-bounce"
                >
                  {team && !canEdit && (
                    <div
                      role="note"
                      className="mb-4 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600"
                    >
                      <HiOutlineLockClosed className="h-4 w-4 shrink-0 text-gray-400" />
                      Only team admins can connect or change integrations. Ask
                      an admin on your team to make changes here.
                    </div>
                  )}
                  {/* A disabled fieldset disables every button and input inside it. */}
                  <fieldset disabled={!canEdit} className="min-w-0">
                    {renderIntegration(selectedProviderId)}
                  </fieldset>
                </div>
              ) : (
                <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-gray-300 text-sm text-gray-400">
                  Select an integration to configure it.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
